import React, { useEffect, useState } from 'react';
import { CreditCard, BarChart, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useSearchParams, useNavigate } from 'react-router-dom';

interface Profile {
  credits_remaining: number | null;
}

interface StripeSubscriptionData {
  hasActiveSubscription: boolean;
  plan?: 'basic' | 'pro';
  currentPeriodEnd?: string;  // Unix timestamp
  amount?: number;            // In cents
  nextInvoiceAmount?: number; // In cents
  cancelAtPeriodEnd?: boolean;
  error?: string;
}

const PlanBadge = ({ plan }: { plan: string }) => {
  const colors = {
    free: 'bg-gray-100 text-gray-800',
    basic: 'bg-blue-100 text-blue-800',
    pro: 'bg-purple-100 text-purple-800'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[plan]}`}>
      {plan.charAt(0).toUpperCase() + plan.slice(1)}
    </span>
  );
};

const ProgressBar = ({ current, total }: { current: number; total: number }) => {
  const percentage = (current / total) * 100;
  
  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div 
        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

async function checkSubscription(email: string) {
  const url = `https://serv.adacadabra.ai/api/stripe/subscription?email=${encodeURIComponent(email)}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      hasActiveSubscription: data.hasActiveSubscription,
      plan: data.plan,
      currentPeriodEnd: data.currentPeriodEnd,
      amount: data.amount,
      nextInvoiceAmount: data.nextInvoiceAmount,
      cancelAtPeriodEnd: data.cancelAtPeriodEnd
    };
  } catch (error) {
    throw error;
  }
}

async function createPortalSession(email: string) {
  const url = `https://serv.adacadabra.ai/api/stripe/create-portal-session?email=${encodeURIComponent(email)}`;
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.url;
  } catch (error) {
    throw error;
  }
}

export default function BillingPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<StripeSubscriptionData>({ 
    hasActiveSubscription: false 
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id || !user?.email) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('credits_remaining')
          .eq('id', user.id)
          .single();

        if (error) {
          throw error;
        }

        setProfile(data);

        // Fetch Stripe subscription data
        const stripeData = await checkSubscription(user.email);
        setSubscriptionStatus(stripeData);
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [user?.id, user?.email]);

  useEffect(() => {
    // Check for success parameter
    if (searchParams.get('success') === 'true') {
      setShowSuccess(true);
      // Remove the success parameter after 5 seconds
      const timer = setTimeout(() => {
        setShowSuccess(false);
        navigate('/billing', { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  const plan = subscriptionStatus.hasActiveSubscription ? subscriptionStatus.plan! : 'free';
  const creditsRemaining = profile?.credits_remaining ?? 0;
  const creditsTotal = plan === 'free' ? 3 : (plan === 'basic' ? 50 : Infinity);
  const creditsUsed = plan === 'pro' ? 0 : (creditsTotal - creditsRemaining);

  const getCreditsDisplay = (value: number) => {
    return value === Infinity ? '∞' : value.toString();
  };

  // Base Stripe URLs with success redirect
  const stripeUrls = {
    basic: 'https://buy.stripe.com/eVa9CWbsxgwAd3O001',
    pro: 'https://buy.stripe.com/9AQ4iC4050xCd3OcMM'
  };

  function getStripeUrl(baseUrl: string, promoCode?: string) {
    const url = new URL(baseUrl);
    if (promoCode) {
      url.searchParams.set('prefilled_promo_code', promoCode);
    }
    if (user?.email) {
      url.searchParams.set('prefilled_email', user.email);
    }
    url.searchParams.set('success_url', `${window.location.origin}/billing?success=true`);
    return url.toString();
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {showSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
            <Check className="w-5 h-5 text-green-500" />
            <span className="text-green-700">
              Thank you for your subscription! Your account has been upgraded.
            </span>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Billing & Usage</h2>
          </div>

          {/* Current Plan */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Current Plan</h3>
                <div className="mt-1 flex items-center space-x-2">
                  <PlanBadge plan={plan} />
                  <span className="text-sm text-gray-600">{subscriptionStatus.hasActiveSubscription ? 'active' : 'inactive'}</span>
                </div>
                {subscriptionStatus.hasActiveSubscription && (
                  <div className="mt-2 space-y-1">
                    {subscriptionStatus.amount && (
                      <p className="text-sm text-gray-600">
                        Current plan: ${(subscriptionStatus.amount / 100).toFixed(2)}/month
                      </p>
                    )}
                    {subscriptionStatus.currentPeriodEnd && (
                      <p className="text-sm text-gray-600">
                        Current period ends: {new Date(parseInt(subscriptionStatus.currentPeriodEnd) * 1000).toLocaleDateString()}
                      </p>
                    )}
                    {subscriptionStatus.nextInvoiceAmount && (
                      <p className="text-sm text-gray-600">
                        Next invoice: ${(subscriptionStatus.nextInvoiceAmount / 100).toFixed(2)}
                      </p>
                    )}
                    {subscriptionStatus.cancelAtPeriodEnd && (
                      <p className="text-sm text-yellow-600">
                        Your subscription will cancel at the end of the current period
                      </p>
                    )}
                  </div>
                )}
              </div>
              {subscriptionStatus.hasActiveSubscription && (
                <a
                  href="https://billing.stripe.com/p/login/4gw3giblO7Sg1eEaEE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Manage Subscription
                </a>
              )}
            </div>
          </div>

          {/* Credits Usage */}
          <div className="px-6 py-4">
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-500">Credits Usage</h3>
              
              <div className="mt-2">
                <ProgressBar current={creditsRemaining} total={creditsTotal} />
                <div className="mt-2 flex justify-between text-sm text-gray-600">
                  <span>{getCreditsDisplay(creditsRemaining)} credits remaining</span>
                  <span>{creditsUsed} credits used</span>
                </div>
              </div>

              {profile?.credits_remaining === 0 && !subscriptionStatus?.hasActiveSubscription && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      {/* Stop icon */}
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-base text-red-800 font-semibold">
                        Account Paused - No Credits Remaining
                      </p>
                      <ul className="mt-2 text-sm text-red-700 space-y-1">
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Basic Plan ($9.99/mo): 50 posts/month
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Pro Plan ($19.99/mo): Unlimited posts
                        </li>
                      </ul>
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="mt-3 inline-flex items-center text-sm font-semibold text-red-700 hover:text-red-600"
                      >
                        Upgrade Now
                        <svg className="ml-1 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {profile?.credits_remaining > 0 && profile?.credits_remaining < 5 && !subscriptionStatus?.hasActiveSubscription && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      {/* Warning icon */}
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-base text-red-800 font-semibold">
                        Running Low: {profile.credits_remaining} {profile.credits_remaining === 1 ? 'Post' : 'Posts'} Remaining
                      </p>
                      <ul className="mt-2 text-sm text-red-700 space-y-1">
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Basic: 50 posts/month
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Pro: Unlimited posts
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Plan Features */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-lg">
            <h3 className="text-sm font-medium text-gray-500 mb-6">Plan Comparison</h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="text-left text-sm font-medium text-gray-500 pb-4">Feature</th>
                    <th className="text-center text-sm font-medium text-gray-500 pb-4">Free</th>
                    <th className="text-center text-sm font-medium text-gray-500 pb-4">Basic</th>
                    <th className="text-center text-sm font-medium text-gray-500 pb-4">Pro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Monthly Posts</td>
                    <td className="py-3 text-center text-sm text-gray-600">3 total</td>
                    <td className="py-3 text-center text-sm text-gray-600">50/month</td>
                    <td className="py-3 text-center text-sm text-gray-600 font-medium">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Cost per Post</td>
                    <td className="py-3 text-center text-sm text-gray-600">Free</td>
                    <td className="py-3 text-center text-sm text-gray-600">~$0.20</td>
                    <td className="py-3 text-center text-sm text-gray-600">Best value</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Post Analysis</td>
                    <td className="py-3 text-center text-sm text-gray-600">3 modes</td>
                    <td className="py-3 text-center text-sm text-gray-600">All modes</td>
                    <td className="py-3 text-center text-sm text-gray-600">All modes</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Voice Recording</td>
                    <td className="py-3 text-center text-sm text-gray-600">✓</td>
                    <td className="py-3 text-center text-sm text-gray-600">✓</td>
                    <td className="py-3 text-center text-sm text-gray-600">✓</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Custom Profile</td>
                    <td className="py-3 text-center text-sm text-gray-600">✓</td>
                    <td className="py-3 text-center text-sm text-gray-600">✓</td>
                    <td className="py-3 text-center text-sm text-gray-600">✓</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Support</td>
                    <td className="py-3 text-center text-sm text-gray-600">Standard</td>
                    <td className="py-3 text-center text-sm text-gray-600">Priority</td>
                    <td className="py-3 text-center text-sm text-gray-600">Priority</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Early Access Features</td>
                    <td className="py-3 text-center text-sm text-gray-600">-</td>
                    <td className="py-3 text-center text-sm text-gray-600">-</td>
                    <td className="py-3 text-center text-sm text-gray-600">✓</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-600">Monthly Price</td>
                    <td className="py-3 text-center text-sm text-gray-600">$0</td>
                    <td className="py-3 text-center text-sm text-gray-600">$9.99</td>
                    <td className="py-3 text-center text-sm text-gray-600 font-medium">$19.99</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {plan === 'free' && (
              <div className="mt-6 flex justify-end space-x-4">
                <button
                  onClick={() => {
                    const url = getStripeUrl(stripeUrls.basic);
                    window.location.href = url;
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100"
                >
                  Basic Plan - $9.99/mo
                </button>
                <button
                  onClick={() => {
                    const url = getStripeUrl(stripeUrls.pro, '100% Off');
                    window.location.href = url;
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Pro Plan - $19.99/mo
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
